/**
 * Safe fetch utilities that read response body exactly once
 * Prevents "body stream already read" errors
 * Handles non-JSON responses (e.g., HTML error pages from 520s)
 */

/**
 * Read response body safely - reads text() once and parses JSON once
 * @param {Response} res - Fetch Response object
 * @returns {Promise<{raw: string, data: any}>}
 */
export async function readResponseSafe(res) {
  const raw = await res.text(); // Read ONCE
  
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (e) {
    // Non-JSON response (e.g., HTML error page from 520)
    data = null;
  }
  
  return { raw, data };
}

/**
 * Fetch with safe response handling
 * - Reads body exactly once
 * - Handles non-JSON responses
 * - Throws with status, raw body, and parsed data
 * 
 * @param {string} url - URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON data
 * @throws {Error} - With status, raw, and data properties
 */
export async function fetchJsonSafe(url, options = {}) {
  const res = await fetch(url, options);
  const { raw, data } = await readResponseSafe(res);
  
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || 
                   raw?.substring(0, 500) || 
                   `Request failed with status ${res.status}`;
    
    console.error(`[FETCH] Error ${res.status}:`, { 
      url, 
      status: res.status, 
      raw: raw?.substring(0, 500), 
      data 
    });
    
    const err = new Error(detail);
    err.status = res.status;
    err.raw = raw;
    err.data = data;
    throw err;
  }
  
  return data;
}

/**
 * Fetch with automatic retry for transient errors (502, 503, 504, 520)
 * 
 * @param {string} url - URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {number} maxRetries - Maximum retry attempts (default: 3)
 * @param {number} baseDelay - Base delay in ms (default: 1000)
 * @returns {Promise<any>} - Parsed JSON data
 */
export async function fetchJsonSafeWithRetry(url, options = {}, maxRetries = 3, baseDelay = 1000) {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchJsonSafe(url, options);
    } catch (err) {
      lastError = err;
      
      // Only retry on transient errors
      const isTransient = [502, 503, 504, 520, 521, 522, 523, 524].includes(err.status);
      
      if (!isTransient || attempt === maxRetries) {
        throw err;
      }
      
      // Exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`[FETCH] Retry ${attempt + 1}/${maxRetries} after ${delay}ms for ${url}`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

export default fetchJsonSafe;
